import { RenewalSummary } from '@policyvision/contracts';
import { Dot } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/common/components/ui/badge';
import { PaginationControls } from '@/common/components/ui/pagination-controls';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/common/components/ui/table';
import { ScrollShadows } from '@/policy/component/ScrollShadows/ScrollShadows';
import { useScroll } from '@/policy/hooks/useScroll';

import { Filters } from '../types/FilterTypes';
import { RenewalStatusList } from '../types/RenewalStatus';
import { RenewalSortField } from '../types/SortTypes';
import { COLORS } from '../utils/badges';
import ComparisonDrawer from './ComparisonDrawer';
import RenewalDrawer from './RenewalDrawer';
import RenewalFilters from './RenewalFilters';
import { RenewalHeader } from './RenewalHeader';
import { RenewalListSkeleton } from './RenewalListSkeleton';
import { RenewalRow } from './RenewalRow';

interface RenewalListProps {
  renewals: RenewalSummary[];
  isLoading: boolean;
  totalPages: number;
  totalRenewals: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSort: (field: RenewalSortField) => void;
  filters: Filters;
  onFilterChange: (value: string | string[] | number[], key: string) => void;
  onClearFilters: () => void;
}
export const RenewalList = ({
  renewals,
  isLoading,
  totalPages,
  totalRenewals,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSort,
  filters,
  onFilterChange,
  onClearFilters,
}: RenewalListProps) => {
  const { t } = useTranslation();
  const {
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    canScrollDown,
    canScrollUp,
  } = useScroll();
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const thead = el.querySelector('thead');
    if (!thead) return;
    const observer = new ResizeObserver(() => {
      setHeaderHeight(thead.getBoundingClientRect().height);
    });
    observer.observe(thead);
    setHeaderHeight(thead.getBoundingClientRect().height);
    return () => observer.disconnect();
  }, [scrollContainerRef]);

  const [selectedRenewal, setSelectedRenewal] = useState<RenewalSummary | null>(
    null,
  );
  const [selectedRenewalIndex, setSelectedRenewalIndex] = useState<
    number | null
  >(null);
  const [isRenewalDrawerOpen, setIsRenewalDrawerOpen] =
    useState<boolean>(false);
  const [isComparisonDrawerOpen, setIsComparisonDrawerOpen] =
    useState<boolean>(false);

  const handleRowClick = (
    renewal: RenewalSummary,
    type: 'comparison' | 'details',
  ) => {
    setSelectedRenewal(renewal);
    if (type === 'details') {
      const newSelectedIndex = renewals.findIndex(
        (r) => r.uuid === renewal.uuid,
      );
      setSelectedRenewalIndex(newSelectedIndex);
      setIsComparisonDrawerOpen(false);
      setIsRenewalDrawerOpen(true);
    } else {
      setIsRenewalDrawerOpen(false);
      setIsComparisonDrawerOpen(true);
    }
  };

  const handleCloseRenewalDrawer = () => {
    setIsRenewalDrawerOpen(false);
    if (selectedRenewal) {
      setSelectedRenewal(null);
      setSelectedRenewalIndex(null);
    }
  };

  const handleCloseComparisonDrawer = () => {
    setIsComparisonDrawerOpen(false);
    if (selectedRenewal) {
      setSelectedRenewal(null);
    }
  };

  const handleDrawerSwitch = () => {
    if (isRenewalDrawerOpen) {
      setIsRenewalDrawerOpen(false);
      setIsComparisonDrawerOpen(true);
    } else if (isComparisonDrawerOpen) {
      setIsComparisonDrawerOpen(false);
      const index = selectedRenewal
        ? renewals.findIndex((r) => r.uuid === selectedRenewal.uuid)
        : null;
      setSelectedRenewalIndex(index !== null && index !== -1 ? index : null);
      setIsRenewalDrawerOpen(true);
    }
  };

  const handleNavigatePrevious = () => {
    if (selectedRenewal) {
      if (selectedRenewalIndex !== null && selectedRenewalIndex > 0) {
        const newSelectedRenewal = renewals[selectedRenewalIndex - 1];
        setSelectedRenewal(newSelectedRenewal);
        setSelectedRenewalIndex(selectedRenewalIndex - 1);
      }
    }
  };

  const handleNavigateNext = () => {
    if (selectedRenewal) {
      if (
        selectedRenewalIndex !== null &&
        selectedRenewalIndex < renewals.length - 1
      ) {
        const newSelectedRenewal = renewals[selectedRenewalIndex + 1];
        setSelectedRenewal(newSelectedRenewal);
        setSelectedRenewalIndex(selectedRenewalIndex + 1);
      }
    }
  };

  const hasPrevious = selectedRenewalIndex !== null && selectedRenewalIndex > 0;
  const hasNext =
    selectedRenewalIndex !== null && selectedRenewalIndex < renewals.length - 1;
  return (
    <div className="flex flex-col h-full w-full">
      <RenewalFilters
        filters={filters}
        onFilterChange={onFilterChange}
        onClearFilters={onClearFilters}
      />
      <ul className="flex items-center gap-2 mb-4 mt-32">
        {RenewalStatusList.map((status, idx) => (
          <li key={`${status}-${idx}`}>
            <Badge variant={COLORS.STATUS[status]}>
              <Dot className="w-4 stroke-10 opacity-70" />
              {t(`renewal.${status}`)}
            </Badge>
          </li>
        ))}
      </ul>

      <ScrollShadows
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
        canScrollDown={canScrollDown}
        canScrollUp={canScrollUp}
        className="rounded-md border border-border bg-background shadow-sm overflow-hidden flex-1 flex flex-col min-h-0 min-w-0"
        offsets={{
          top: headerHeight,
          left: 0,
          bottom: 0,
          right: 0,
        }}
      >
        <div
          ref={scrollContainerRef}
          className={`overflow-x-auto overflow-y-auto flex-1 z-0
          [&::-webkit-scrollbar]:h-1.5
          [&::-webkit-scrollbar]:w-1.5
          [&::-webkit-scrollbar-track]:bg-transparent
          [&::-webkit-scrollbar-thumb]:bg-warm-gray-400
          [&::-webkit-scrollbar-thumb]:rounded-full`}
        >
          <Table noWrapper className="w-full min-w-full">
            <RenewalHeader onSort={onSort} />
            {isLoading ? (
              <RenewalListSkeleton rowCount={pageSize} />
            ) : (
              <TableBody>
                {renewals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={17} className="text-center">
                      {t('renewal.noRenewalsFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  renewals.map((renewal: RenewalSummary) => (
                    <RenewalRow
                      key={renewal.uuid}
                      renewal={renewal}
                      onRowClick={(type) => handleRowClick(renewal, type)}
                    />
                  ))
                )}
              </TableBody>
            )}
          </Table>
        </div>
      </ScrollShadows>

      <div className="pt-4 shrink-0">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalRenewals}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </div>
      <RenewalDrawer
        renewal={selectedRenewal}
        isOpen={isRenewalDrawerOpen}
        onClose={handleCloseRenewalDrawer}
        handleDrawerSwitch={handleDrawerSwitch}
        onNavigatePrevious={handleNavigatePrevious}
        onNavigateNext={handleNavigateNext}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
      />
      <ComparisonDrawer
        renewal={selectedRenewal}
        isOpen={isComparisonDrawerOpen}
        onClose={handleCloseComparisonDrawer}
      />
    </div>
  );
};
